import NumberInput from "@components/common/hookform/NumberInput";
import { logger } from "@justhome/common";
import { MaximumBudget, usePostMaximumBudget } from "@justhome/common/api/core";
import {
  AppModal,
  Button,
  InlineNotification,
  Title,
} from "@justhome/react-ui";
import { useApplicationTranslation } from "@lib/hooks/translations";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import { useState } from "react";
import { useForm } from "react-hook-form";

const MaximumBudgetEditModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useApplicationTranslation();
  const { state, dispatch } = useApplicationContext();
  const { partner } = usePartnerContext();
  const {
    mutateAsync: postMaximumBudget,
    isLoading,
    isError,
  } = usePostMaximumBudget({
    onSuccess: (maximumBudget) => {
      dispatch({ type: "SET_MAXIMUM_BUDGET", maximumBudget });
    },
    onError: (err) => {
      logger.error("Failed to post maximum budget", err);
    },
  });

  const onSubmit = async (values: MaximumBudget) => {
    const { id: _, ...body } = values;
    await postMaximumBudget({
      body: {
        ...body,
        leadId: state.lead?.id,
        partnerId: partner?.id,
      },
    });
    setIsOpen(false);
  };

  const { control, handleSubmit } = useForm<MaximumBudget>({
    mode: "onBlur",
    defaultValues: {
      ...state.maximumBudget,
    },
  });

  return (
    <div className="border-t border-neutral-50 px-3xs flex items-center justify-end w-full py-3xs">
      <Button
        size="xs"
        variant="ghost"
        iconEnd="Edit"
        onClick={() => setIsOpen(true)}
      >
        {t("edit_details_label")}
      </Button>
      <AppModal
        className="w-full max-w-lg"
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <AppModal.Title closable>
          <Title as="h3" className="text-h2">
            {t("edit_details_label")}
          </Title>
        </AppModal.Title>
        <AppModal.Content>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid grid-cols-2 gap items-end"
          >
            <NumberInput
              className="col-span-2"
              disabled={isLoading}
              label={t("down_payment_label")}
              placeholder={t("down_payment_placeholder")}
              name="equity"
              control={control}
              size="md"
              suffix="€"
              rules={{
                required: { value: true, message: t("error_down_payment") },
                min: 0,
              }}
              info={t("down_payment_info")}
            />
            <NumberInput
              className="col-span-2"
              disabled={isLoading}
              label={t("monthly_net_income_label")}
              placeholder={t("monthly_net_income_placeholder")}
              name="monthlyNetIncome"
              control={control}
              size="md"
              suffix="€"
              rules={{
                required: {
                  value: true,
                  message: t("error_monthly_net_income"),
                },
                min: {
                  value: 1800,
                  message: t("error_monthly_net_income"),
                },
              }}
              info={t("monthly_net_income_info")}
            />
            {isError && (
              <InlineNotification
                className="col-span-2 mt-2s mb-s"
                type="error"
              >
                {t("generic_error")}
              </InlineNotification>
            )}
            <div className="col-span-2 flex justify-start gap-2s text-center">
              <Button
                variant="outline_primary"
                type="button"
                className="!w-auto"
                size="md"
                disabled={isLoading}
                onClick={() => setIsOpen(false)}
              >
                {t("cancel_button")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="!w-auto"
                size="md"
                isLoading={isLoading}
              >
                {t("save_changes_button")}
              </Button>
            </div>
          </form>
        </AppModal.Content>
      </AppModal>
    </div>
  );
};

export default MaximumBudgetEditModal;
